// Google Maps API Usage Logger
// Tracks all API calls with timestamp, place ID, request type, fields, estimated cost, and user info
// Now syncs to backend for centralized cost tracking
//
// Updated for Google Places API (New) pricing effective March 2025:
// - Monthly free tier per SKU (not a $200 credit)
// - Frontend tracks estimated costs (backend handles actual billing)

const STORAGE_KEY = 'googleMapsApiLogs';
const PENDING_SYNC_KEY = 'googleMapsApiLogsPendingSync';
const SKU_USAGE_KEY = 'googleMapsSkuUsage'; // Track local SKU usage for UI feedback
const MAX_LOGS = 1000; // Keep last 1000 entries to prevent storage bloat
const SYNC_BATCH_SIZE = 50; // Send logs in batches
const SYNC_INTERVAL_MS = 30000; // Sync every 30 seconds

// Current user context for logging
let currentUser = null;
let syncTimer = null;

// Set the current user for logging (call this when user logs in/out)
export function setCurrentUser(user) {
  currentUser = user ? {
    userId: user.id || user.firebaseUid,
    username: user.username
  } : null;
}

// Get the API base URL (strip trailing /api if present since we add it ourselves)
function getApiBaseUrl() {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  return baseUrl.replace(/\/api\/?$/, '');
}

// Get pending logs that need to be synced
function getPendingLogs() {
  try {
    const stored = localStorage.getItem(PENDING_SYNC_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    return [];
  }
}

// Save pending logs
function savePendingLogs(logs) {
  try {
    localStorage.setItem(PENDING_SYNC_KEY, JSON.stringify(logs));
  } catch (e) {
    console.error('Failed to save pending logs:', e);
  }
}

// Add log to pending sync queue
function addToPendingSync(logEntry) {
  const pending = getPendingLogs();
  pending.push(logEntry);
  savePendingLogs(pending);
}

// Sync pending logs to backend
async function syncLogsToBackend() {
  const pending = getPendingLogs();
  if (pending.length === 0) return;

  // Don't sync if no user is logged in (endpoint requires auth)
  if (!currentUser?.userId) return;

  // Get auth token from localStorage
  const token = localStorage.getItem('authToken');
  if (!token) return;

  // Take a batch
  const batch = pending.slice(0, SYNC_BATCH_SIZE);
  const remaining = pending.slice(SYNC_BATCH_SIZE);

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/costs/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ logs: batch }),
    });

    if (response.ok) {
      // Remove synced logs from pending
      savePendingLogs(remaining);

      // If more remaining, schedule another sync
      if (remaining.length > 0) {
        setTimeout(syncLogsToBackend, 1000);
      }
    }
  } catch (e) {
    // Network error - keep logs in pending, they'll sync later
    console.debug('Failed to sync API logs to backend:', e.message);
  }
}

// Start periodic sync
function startPeriodicSync() {
  if (syncTimer) return;

  syncTimer = setInterval(syncLogsToBackend, SYNC_INTERVAL_MS);

  // Also sync on page visibility change (when user comes back to tab)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        syncLogsToBackend();
      }
    });
  }

  // Sync on page unload
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', syncLogsToBackend);
  }
}

// Initialize sync on module load
if (typeof window !== 'undefined') {
  startPeriodicSync();
  // Initial sync after a short delay
  setTimeout(syncLogsToBackend, 5000);
}

// Monthly free tier limits per SKU (effective March 2025)
// https://developers.google.com/maps/billing-and-pricing/pricing
const FREE_TIER_LIMITS = {
  // Essentials tier - 10,000 free/month
  'PlaceDetails-Essentials': 10000,
  'PlaceDetails-IDsOnly': Infinity,  // Unlimited free
  'TextSearch-IDsOnly': Infinity,    // Unlimited free
  'TextSearch-Essentials': 10000,
  'Autocomplete': 10000,
  'Autocomplete-Session': 10000,     // Autocomplete (New) session-based: 10,000 free/month
  'Geocode': 10000,
  'ReverseGeocode': 10000,

  // Pro tier - 5,000 free/month
  'PlaceDetails-Pro': 5000,
  'TextSearch-Pro': 5000,
  'NearbySearch-Pro': 5000,

  // Enterprise tier - 1,000 free/month
  'PlaceDetails-Enterprise': 1000,
  'PlaceDetails-Atmosphere': 1000,
  'PlaceDetails-Photos': 1000,
  'TextSearch-Enterprise': 1000,
  'TextSearch-Atmosphere': 1000,
  'NearbySearch-Enterprise': 1000,
  'NearbySearch-Atmosphere': 1000,

  // Legacy SKU mappings (map to appropriate tier)
  'PlaceDetails-Basic': 5000,       // Maps to Pro tier
  'PlaceDetails-Contact': 1000,     // Maps to Enterprise tier
  'PlaceDetails-Status': 1000,      // Maps to Enterprise tier (opening_hours)
  'PlaceDetails-Refresh': 1000,     // Maps to Enterprise tier
  'TextSearch': 5000,               // Maps to Pro tier
  'NearbySearch': 5000,             // Maps to Pro tier
};

// Google Maps API pricing (per request) - Updated for Places API (New) March 2025
// https://developers.google.com/maps/billing-and-pricing/pricing
// Note: Place Details uses TIER-BASED pricing - you pay the highest applicable tier, NOT additive
const API_COSTS = {
  // Places API (New) - Place Details (tier-based pricing - highest tier wins)
  'PlaceDetails-IDsOnly': 0,        // Free - IDs only
  'PlaceDetails-Essentials': 0.005, // $5/1000 - Basic fields (id, displayName, types, formattedAddress, location)
  'PlaceDetails-Pro': 0.017,        // $17/1000 - Pro fields (photos, businessStatus, addressComponents)
  'PlaceDetails-Enterprise': 0.02,  // $20/1000 - Enterprise fields (regularOpeningHours, websiteUri, rating)
  'PlaceDetails-Atmosphere': 0.025, // $25/1000 - Enterprise + Atmosphere (reviews, priceLevel, etc.)
  'PlaceDetails-Photos': 0.007,     // $7/1000 - Photo requests

  // Legacy keys for backward compatibility (mapped to correct tiers)
  'PlaceDetails-Basic': 0.017,      // Maps to Pro tier
  'PlaceDetails-Contact': 0.02,     // Maps to Enterprise tier (opening_hours, phone, website)

  // Pre-calculated costs for common request patterns
  'PlaceDetails-Status': 0.02,      // $20/1000 - Enterprise tier (opening_hours triggers Enterprise)
  'PlaceDetails-Refresh': 0.02,     // $20/1000 - Enterprise tier

  // Places API (New) - Text Search
  'TextSearch-IDsOnly': 0,          // Free - IDs only
  'TextSearch-Essentials': 0.005,   // $5/1000
  'TextSearch-Pro': 0.032,          // $32/1000
  'TextSearch-Enterprise': 0.035,   // $35/1000
  'TextSearch-Atmosphere': 0.040,   // $40/1000
  'TextSearch': 0.032,              // Legacy - maps to Pro

  // Places API (New) - Nearby Search
  'NearbySearch-Pro': 0.032,        // $32/1000
  'NearbySearch-Enterprise': 0.035, // $35/1000
  'NearbySearch-Atmosphere': 0.040, // $40/1000
  'NearbySearch': 0.032,            // Legacy - maps to Pro

  // Autocomplete (New) - uses session-based billing
  'Autocomplete': 0.00283,          // $2.83/1000 (per request, legacy)
  'Autocomplete-Session': 0.003,    // $3/1000 (session-based, new API)

  // Geocoding API
  'Geocode': 0.005,                 // $5/1000
  'ReverseGeocode': 0.005,          // $5/1000
};

// Field categories for Place Details tier-based cost calculation
// Essentials tier fields ($5/1000)
const ESSENTIALS_FIELDS = [
  'place_id', 'name', 'types', 'formatted_address', 'geometry', 'icon',
  'icon_mask_base_uri', 'icon_background_color', 'url', 'vicinity'
];

// Pro tier fields ($17/1000)
const PRO_FIELDS = [
  'address_component', 'address_components', 'adr_address', 'business_status',
  'photo', 'photos', 'plus_code', 'type', 'utc_offset', 'utc_offset_minutes'
];

// Enterprise tier fields ($20/1000)
const ENTERPRISE_FIELDS = [
  'formatted_phone_number', 'international_phone_number', 'opening_hours',
  'current_opening_hours', 'secondary_opening_hours', 'website',
  'curbside_pickup', 'delivery', 'dine_in', 'editorial_summary', 'price_level',
  'rating', 'reservable', 'reviews', 'serves_beer', 'serves_breakfast',
  'serves_brunch', 'serves_dinner', 'serves_lunch', 'serves_vegetarian_food',
  'serves_wine', 'takeout', 'user_ratings_total'
];

// Calculate estimated cost for a Place Details request based on fields
// Uses TIER-BASED pricing - you pay the highest applicable tier, not additive
function calculatePlaceDetailsCost(fields) {
  if (!fields || fields.length === 0) {
    // Default fields typically include Enterprise-level fields
    return API_COSTS['PlaceDetails-Enterprise'];
  }

  // Determine highest tier needed based on requested fields
  let tier = 'Essentials';

  for (const field of fields) {
    if (ENTERPRISE_FIELDS.includes(field)) {
      // Enterprise is highest tier, no need to check further
      return API_COSTS['PlaceDetails-Enterprise'];
    }
    if (PRO_FIELDS.includes(field)) {
      tier = 'Pro';
    }
  }

  return API_COSTS[`PlaceDetails-${tier}`];
}

// Get current month key (YYYY-MM)
function getCurrentMonthKey() {
  return new Date().toISOString().slice(0, 7);
}

// Get local SKU usage for the current month
function getLocalSkuUsage() {
  try {
    const stored = localStorage.getItem(SKU_USAGE_KEY);
    if (!stored) return { monthKey: getCurrentMonthKey(), usage: {} };

    const data = JSON.parse(stored);
    // Reset if month changed
    if (data.monthKey !== getCurrentMonthKey()) {
      return { monthKey: getCurrentMonthKey(), usage: {} };
    }
    return data;
  } catch (e) {
    return { monthKey: getCurrentMonthKey(), usage: {} };
  }
}

// Save local SKU usage
function saveLocalSkuUsage(data) {
  try {
    localStorage.setItem(SKU_USAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('Failed to save SKU usage:', e);
  }
}

// Increment SKU usage and check if billable (local estimate)
// Note: Backend is authoritative for actual billing
function incrementLocalSkuUsage(requestType) {
  const data = getLocalSkuUsage();
  const currentCount = data.usage[requestType] || 0;
  const newCount = currentCount + 1;
  const freeLimit = FREE_TIER_LIMITS[requestType] ?? 0;

  // Update local count
  data.usage[requestType] = newCount;
  saveLocalSkuUsage(data);

  // Determine if billable (local estimate)
  const isBillable = freeLimit !== Infinity && newCount > freeLimit;

  return {
    isBillable,
    usageCount: newCount,
    freeLimit: freeLimit === Infinity ? 'unlimited' : freeLimit,
  };
}

// Calculate billable cost considering free tier (local estimate)
function calculateBillableCost(requestType, baseCost) {
  const data = getLocalSkuUsage();
  const currentCount = data.usage[requestType] || 0;
  const freeLimit = FREE_TIER_LIMITS[requestType] ?? 0;

  // If within free tier, cost is 0
  if (freeLimit === Infinity || currentCount < freeLimit) {
    return 0;
  }

  return baseCost;
}

// Export free tier status for UI display
export function getFreeTierStatus() {
  const data = getLocalSkuUsage();
  const status = {};

  for (const [sku, limit] of Object.entries(FREE_TIER_LIMITS)) {
    const used = data.usage[sku] || 0;
    const remaining = limit === Infinity ? Infinity : Math.max(0, limit - used);
    const percentUsed = limit === Infinity ? 0 : Math.round((used / limit) * 100);

    status[sku] = {
      used,
      limit: limit === Infinity ? 'unlimited' : limit,
      remaining: remaining === Infinity ? 'unlimited' : remaining,
      percentUsed,
      exhausted: limit !== Infinity && used >= limit,
    };
  }

  return {
    monthKey: data.monthKey,
    status,
  };
}

// Get logs from localStorage
function getLogs() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    console.error('Failed to read API logs:', e);
    return [];
  }
}

// Save logs to localStorage
function saveLogs(logs) {
  try {
    // Keep only the most recent MAX_LOGS entries
    const trimmedLogs = logs.slice(-MAX_LOGS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedLogs));
  } catch (e) {
    console.error('Failed to save API logs:', e);
  }
}

// Log an API call with free tier awareness
export function logApiCall({ requestType, placeId = null, fields = null, query = null, location = null, extra = {} }) {
  const timestamp = new Date().toISOString();

  // Calculate base cost (before free tier consideration)
  let baseCost = 0;
  switch (requestType) {
    case 'PlaceDetails':
      baseCost = calculatePlaceDetailsCost(fields);
      break;
    case 'PlaceDetails-Status':
      baseCost = API_COSTS['PlaceDetails-Status'];
      break;
    case 'PlaceDetails-Photos':
      baseCost = API_COSTS['PlaceDetails-Photos'] || calculatePlaceDetailsCost(fields);
      break;
    case 'TextSearch':
      baseCost = API_COSTS['TextSearch'];
      break;
    case 'NearbySearch':
      baseCost = API_COSTS['NearbySearch'];
      break;
    case 'Autocomplete-Session':
      baseCost = API_COSTS['Autocomplete-Session'];
      break;
    case 'Autocomplete':
      baseCost = API_COSTS['Autocomplete'];
      break;
    case 'Geocode':
      baseCost = API_COSTS['Geocode'];
      break;
    case 'ReverseGeocode':
      baseCost = API_COSTS['ReverseGeocode'];
      break;
    default:
      baseCost = API_COSTS[requestType] || 0;
  }

  // Increment SKU usage and check if billable (local estimate)
  const { isBillable, usageCount, freeLimit } = incrementLocalSkuUsage(requestType);

  // Calculate billable cost (0 if within free tier)
  const estimatedCost = isBillable ? baseCost : 0;

  const logEntry = {
    timestamp,
    requestType,
    placeId,
    fields,
    query,
    location: location ? { lat: location.lat?.() || location.lat, lng: location.lng?.() || location.lng } : null,
    baseCost,                              // Cost before free tier
    estimatedCost,                         // Actual billable cost (0 if within free tier)
    isBillable,                            // Whether this exceeded free tier
    usageCount,                            // Current month usage count for this SKU
    freeLimit,                             // Free tier limit for this SKU
    userId: currentUser?.userId || null,
    username: currentUser?.username || null,
    ...extra
  };

  // Save to localStorage (for local viewing/export)
  const logs = getLogs();
  logs.push(logEntry);
  saveLogs(logs);

  // Also add to pending sync queue (for backend tracking)
  addToPendingSync(logEntry);

  return logEntry;
}

// Get all logged API calls
export function getApiLogs() {
  return getLogs();
}

// Clear all logs
export function clearApiLogs() {
  localStorage.removeItem(STORAGE_KEY);
}

// Get summary statistics with free tier awareness
export function getApiLogsSummary() {
  const logs = getLogs();
  const freeTierData = getFreeTierStatus();

  if (logs.length === 0) {
    return {
      totalCalls: 0,
      totalEstimatedCost: 0,
      totalBaseCost: 0,
      freeTierSavings: 0,
      byType: {},
      byDay: {},
      byUser: {},
      freeTierStatus: freeTierData.status,
    };
  }

  const summary = {
    totalCalls: logs.length,
    totalEstimatedCost: 0,      // Actual billable cost
    totalBaseCost: 0,           // Cost without free tier
    freeTierSavings: 0,         // Amount saved by free tier
    billableRequests: 0,
    freeRequests: 0,
    byType: {},
    byDay: {},
    byPlaceId: {},
    byUser: {},
    oldestLog: logs[0]?.timestamp,
    newestLog: logs[logs.length - 1]?.timestamp,
    freeTierStatus: freeTierData.status,
  };

  logs.forEach(log => {
    const baseCost = log.baseCost || log.estimatedCost || 0;
    const billableCost = log.estimatedCost || 0;

    // Total costs
    summary.totalBaseCost += baseCost;
    summary.totalEstimatedCost += billableCost;

    // Track billable vs free requests
    if (log.isBillable === false || billableCost === 0) {
      summary.freeRequests++;
    } else {
      summary.billableRequests++;
    }

    // By type
    if (!summary.byType[log.requestType]) {
      summary.byType[log.requestType] = {
        count: 0,
        cost: 0,
        baseCost: 0,
        freeCount: 0,
        billableCount: 0,
      };
    }
    summary.byType[log.requestType].count++;
    summary.byType[log.requestType].cost += billableCost;
    summary.byType[log.requestType].baseCost += baseCost;
    if (log.isBillable === false || billableCost === 0) {
      summary.byType[log.requestType].freeCount++;
    } else {
      summary.byType[log.requestType].billableCount++;
    }

    // By day
    const day = log.timestamp?.split('T')[0];
    if (day) {
      if (!summary.byDay[day]) {
        summary.byDay[day] = { count: 0, cost: 0, baseCost: 0 };
      }
      summary.byDay[day].count++;
      summary.byDay[day].cost += billableCost;
      summary.byDay[day].baseCost += baseCost;
    }

    // By placeId (top consumers)
    if (log.placeId) {
      if (!summary.byPlaceId[log.placeId]) {
        summary.byPlaceId[log.placeId] = { count: 0, cost: 0 };
      }
      summary.byPlaceId[log.placeId].count++;
      summary.byPlaceId[log.placeId].cost += billableCost;
    }

    // By user
    const userKey = log.username || log.userId || 'anonymous';
    if (!summary.byUser[userKey]) {
      summary.byUser[userKey] = { count: 0, cost: 0, userId: log.userId };
    }
    summary.byUser[userKey].count++;
    summary.byUser[userKey].cost += billableCost;
  });

  // Calculate free tier savings
  summary.freeTierSavings = summary.totalBaseCost - summary.totalEstimatedCost;

  return summary;
}

// Export logs as CSV with free tier information
export function exportLogsAsCsv() {
  const logs = getLogs();

  if (logs.length === 0) {
    return null;
  }

  const headers = [
    'Timestamp', 'Request Type', 'Place ID', 'Fields', 'Query', 'Location',
    'Base Cost ($)', 'Billable Cost ($)', 'Is Billable', 'Usage Count', 'Free Limit',
    'User ID', 'Username'
  ];
  const rows = logs.map(log => [
    log.timestamp,
    log.requestType,
    log.placeId || '',
    log.fields?.join('; ') || '',
    log.query || '',
    log.location ? `${log.location.lat},${log.location.lng}` : '',
    (log.baseCost || log.estimatedCost)?.toFixed(4) || '0',
    log.estimatedCost?.toFixed(4) || '0',
    log.isBillable ?? 'unknown',
    log.usageCount || '',
    log.freeLimit || '',
    log.userId || '',
    log.username || ''
  ]);

  const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

  // Create downloadable blob
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `google-maps-api-logs-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return csv;
}

// Print summary to console (useful for debugging)
export function printApiLogsSummary() {
  const summary = getApiLogsSummary();

  console.group('📊 Google Maps API Usage Summary');
  console.log(`Total API Calls: ${summary.totalCalls}`);
  console.log(`  Free Tier Requests: ${summary.freeRequests}`);
  console.log(`  Billable Requests: ${summary.billableRequests}`);
  console.log(`Total Base Cost (before free tier): $${summary.totalBaseCost.toFixed(2)}`);
  console.log(`Total Billable Cost: $${summary.totalEstimatedCost.toFixed(2)}`);
  console.log(`Free Tier Savings: $${summary.freeTierSavings.toFixed(2)}`);
  console.log(`Date Range: ${summary.oldestLog} to ${summary.newestLog}`);

  console.group('By Request Type:');
  Object.entries(summary.byType)
    .sort((a, b) => b[1].cost - a[1].cost)
    .forEach(([type, data]) => {
      const freeTierInfo = summary.freeTierStatus?.[type];
      const freeTierStr = freeTierInfo
        ? ` (${freeTierInfo.used}/${freeTierInfo.limit} free used)`
        : '';
      console.log(`  ${type}: ${data.count} calls (${data.freeCount} free, ${data.billableCount} billable), $${data.cost.toFixed(2)}${freeTierStr}`);
    });
  console.groupEnd();

  console.group('Free Tier Status:');
  Object.entries(summary.freeTierStatus || {})
    .filter(([_, status]) => status.used > 0)
    .sort((a, b) => b[1].percentUsed - a[1].percentUsed)
    .forEach(([sku, status]) => {
      const statusIcon = status.exhausted ? '🔴' : status.percentUsed > 80 ? '🟡' : '🟢';
      console.log(`  ${statusIcon} ${sku}: ${status.used}/${status.limit} (${status.percentUsed}%)`);
    });
  console.groupEnd();

  console.group('By Day:');
  Object.entries(summary.byDay)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .slice(0, 7) // Last 7 days
    .forEach(([day, data]) => {
      console.log(`  ${day}: ${data.count} calls, $${data.cost.toFixed(2)} (base: $${data.baseCost.toFixed(2)})`);
    });
  console.groupEnd();

  console.group('Top 10 Places by Cost:');
  Object.entries(summary.byPlaceId)
    .sort((a, b) => b[1].cost - a[1].cost)
    .slice(0, 10)
    .forEach(([placeId, data]) => {
      console.log(`  ${placeId}: ${data.count} calls, $${data.cost.toFixed(2)}`);
    });
  console.groupEnd();

  console.group('By User:');
  Object.entries(summary.byUser)
    .sort((a, b) => b[1].cost - a[1].cost)
    .forEach(([username, data]) => {
      const userInfo = data.userId ? `${username} (${data.userId})` : username;
      console.log(`  ${userInfo}: ${data.count} calls, $${data.cost.toFixed(2)}`);
    });
  console.groupEnd();

  console.groupEnd();

  return summary;
}

// Expose functions globally for easy console access
if (typeof window !== 'undefined') {
  window.googleMapsApiLogs = {
    getLogs: getApiLogs,
    getSummary: getApiLogsSummary,
    printSummary: printApiLogsSummary,
    exportCsv: exportLogsAsCsv,
    clear: clearApiLogs,
    getFreeTierStatus,
    FREE_TIER_LIMITS,
    API_COSTS,
  };
}
